import { type PDFDocumentProxy } from 'pdfjs-dist';
import { type MutableRefObject, useCallback, useEffect, useRef } from 'react';

import { DEFAULT_PDF_PAGE_HEIGHT, type PDFPagesVirtualizedListProps } from '../components/PDFPagesVirtualizedList';
import { type PDFPagesCache } from '../utils/PDFPagesCache';
import { PDFPagesViewportsCache } from '../utils/PDFPagesViewportsCache';

export type UsePDFPagesViewportsCacheParams = {
  pdfDocument: PDFDocumentProxy | undefined | null;
  currentScale: number;
  pdfPagesCacheRef: MutableRefObject<PDFPagesCache | undefined>;
};

export const useGetPDFPageHeight = ({
  pdfDocument,
  pdfPagesCacheRef,
  currentScale,
}: UsePDFPagesViewportsCacheParams) => {
  const pdfPagesViewportsCacheRef = useRef<PDFPagesViewportsCache>();

  useEffect(() => {
    pdfPagesViewportsCacheRef.current = new PDFPagesViewportsCache();

    if (pdfDocument) {
      for (let i = 0; i < pdfDocument.numPages; i++) {
        const page = pdfPagesCacheRef.current?.getLoadedPage(i + 1);
        if (page) {
          pdfPagesViewportsCacheRef.current.generateViewportForPage(page, currentScale);
        }
      }
    }
  }, [currentScale, pdfDocument, pdfPagesCacheRef]);

  const getPageHeight = useCallback<PDFPagesVirtualizedListProps['getPageHeight']>(
    (index) => {
      const cachedViewport = pdfPagesViewportsCacheRef.current?.getViewportForPage(index + 1);
      if (cachedViewport) {
        return cachedViewport.height;
      } else {
        pdfDocument?.getPage(index + 1).then((page) => {
          pdfPagesViewportsCacheRef.current?.generateViewportForPage(page, currentScale);
        });
        const viewportForFirstPage = pdfPagesViewportsCacheRef.current?.getViewportForPage(index + 1);
        if (viewportForFirstPage) {
          return viewportForFirstPage.height;
        }
      }
      return DEFAULT_PDF_PAGE_HEIGHT;
    },
    [currentScale, pdfDocument],
  );

  return {
    pdfPagesViewportsCacheRef,
    getPageHeight,
  };
};
